import { useMemo, useState } from 'react'
import { useCollection } from '@cloudscape-design/collection-hooks'
import Alert from '@cloudscape-design/components/alert'
import Badge from '@cloudscape-design/components/badge'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Cards from '@cloudscape-design/components/cards'
import Header from '@cloudscape-design/components/header'
import Link from '@cloudscape-design/components/link'
import Pagination from '@cloudscape-design/components/pagination'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Spinner from '@cloudscape-design/components/spinner'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import { useNavigate } from 'react-router'
import type { RunSummaryGql } from '@/lib/types'
import { useListRuns, useStartCampaign } from '@/hooks/useRuns'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { formatDateTime } from '@/utils/format'
import { getContentTypeLabel } from '@/utils/contentType'

// ─── Local alias ───────────────────────────────────────────────────────────────

type RunRow = RunSummaryGql

// ─── Props ─────────────────────────────────────────────────────────────────────

interface HistoryTabProps {
  clientId: string
  onViewRun: (runId: string) => void
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_TYPE_MAP: Record<
  'running' | 'complete' | 'error' | 'stopped',
  'success' | 'in-progress' | 'error' | 'stopped'
> = {
  complete: 'success',
  running: 'in-progress',
  error: 'error',
  stopped: 'stopped',
}

function formatDuration(startedAt?: string | null, completedAt?: string | null): string {
  if (!startedAt || !completedAt) return '—'
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime()
  if (ms < 0) return '—'
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function HistoryTab({ clientId, onViewRun }: HistoryTabProps) {
  const { runs, loading, error } = useListRuns(clientId)
  const { startCampaign, loading: retryLoading } = useStartCampaign()
  const navigate = useNavigate()
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const [statusFilter, setStatusFilter] = useState<'all' | 'complete' | 'error' | 'running'>('all')

  const filteredRuns = useMemo(() => {
    const all = (runs ?? []) as RunRow[]
    if (statusFilter === 'all') return all
    return all.filter(r => r.status === statusFilter)
  }, [runs, statusFilter])

  const { items, collectionProps, paginationProps } = useCollection(filteredRuns, {
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: 'startedAt' },
        isDescending: true,
      },
    },
    pagination: { pageSize: isMobile ? 10 : 20 },
  })

  if (loading) {
    return <Spinner />
  }

  if (error) {
    return (
      <Alert type="error">
        {error.message ?? 'Failed to load campaign history.'}
      </Alert>
    )
  }

  const hasRunning = (runs as RunRow[]).some(r => r.status === 'running')

  const handleRetry = async (item: RunRow) => {
    const types = item.selectedContentTypes ?? undefined
    await startCampaign(clientId, types as string[] | undefined)
    navigate(`/workspace/clients/${clientId}/campaigns`)
  }

  const runningRun = hasRunning ? (runs as RunRow[]).find(r => r.status === 'running') : undefined
  const runningId = runningRun?.id

  if (isMobile) {
    return (
      <SpaceBetween size="m">
        {hasRunning && (
          <Alert type="info">
            A campaign is currently running.{' '}
            {runningId && (
              <Link onFollow={() => onViewRun(runningId)}>View progress →</Link>
            )}
          </Alert>
        )}

        <SpaceBetween direction="horizontal" size="xs">
          {(['all', 'complete', 'error', 'running'] as const).map(s => (
            <Button
              key={s}
              variant={statusFilter === s ? 'primary' : 'normal'}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'All' : s === 'complete' ? 'Complete' : s === 'error' ? 'Error' : 'Running'}
            </Button>
          ))}
        </SpaceBetween>

        <Cards
          {...collectionProps}
          items={items}
          cardsPerRow={[{ cards: 1 }]}
          header={<Header counter={`(${filteredRuns.length})`}>Campaign History</Header>}
          cardDefinition={{
            header: (item: RunRow) => {
              const val = item.startedAt
              return (
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <Box variant="strong">{val ? formatDateTime(val) : '—'}</Box>
                  <StatusIndicator type={STATUS_TYPE_MAP[item.status]}>
                    {item.status === 'complete' ? 'Complete'
                      : item.status === 'running' ? 'Running'
                      : item.status === 'error' ? 'Error' : 'Stopped'}
                  </StatusIndicator>
                  {item.trigger === 'auto' && <Badge color="blue">Auto</Badge>}
                </SpaceBetween>
              )
            },
            sections: [
              {
                id: 'types',
                header: 'Content Types',
                content: (item: RunRow) => {
                  const types = item.selectedContentTypes
                  if (!types || types.length === 0) return <Box color="text-body-secondary">—</Box>
                  return (
                    <SpaceBetween direction="horizontal" size="xxs">
                      {types.map((ct) => (
                        <Badge key={ct} color="grey">{getContentTypeLabel(ct)}</Badge>
                      ))}
                    </SpaceBetween>
                  )
                },
              },
              {
                id: 'stats',
                content: (item: RunRow) => {
                  const count = item.resultsCount
                  const runId = item.id
                  const cost = item.estimatedCostUsd
                  return (
                    <SpaceBetween direction="horizontal" size="xl">
                      <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Pieces</Box>
                        {count && runId
                          ? <Link onFollow={() => void navigate(`/workspace/clients/${clientId}/queue?runId=${runId}`)}>{String(count)}</Link>
                          : <Box>{count ? String(count) : '—'}</Box>}
                      </SpaceBetween>
                      <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Duration</Box>
                        <Box>{formatDuration(item.startedAt, item.completedAt)}</Box>
                      </SpaceBetween>
                      <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Cost</Box>
                        <Box>{cost != null ? `$${cost.toFixed(2)}` : '—'}</Box>
                      </SpaceBetween>
                    </SpaceBetween>
                  )
                },
              },
              {
                id: 'actions',
                content: (item: RunRow) => {
                  const runId = item.id ?? ''
                  return (
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="inline-link" onClick={() => onViewRun(runId)}>View</Button>
                      {item.status === 'error' && (
                        <Button
                          variant="inline-link"
                          disabled={retryLoading}
                          onClick={() => { void handleRetry(item) }}
                        >
                          Retry
                        </Button>
                      )}
                    </SpaceBetween>
                  )
                },
              },
            ],
          }}
          empty={
            <Box textAlign="center" padding="l">
              <SpaceBetween size="s" direction="vertical">
                <Box variant="p">No campaign runs yet.</Box>
                <Button variant="primary" onClick={() => onViewRun('')}>
                  Run your first campaign →
                </Button>
              </SpaceBetween>
            </Box>
          }
        />

        <Pagination {...paginationProps} />
      </SpaceBetween>
    )
  }

  return (
    <SpaceBetween size="m">
      {hasRunning && (
        <Alert type="info">
          A campaign is currently running.{' '}
          {runningId && (
            <Link onFollow={() => onViewRun(runningId)}>View progress →</Link>
          )}
        </Alert>
      )}

      <SpaceBetween direction="horizontal" size="xs">
        {(['all', 'complete', 'error', 'running'] as const).map(s => (
          <Button
            key={s}
            variant={statusFilter === s ? 'primary' : 'normal'}
            onClick={() => setStatusFilter(s)}
          >
            {s === 'all' ? 'All' : s === 'complete' ? 'Complete' : s === 'error' ? 'Error' : 'Running'}
          </Button>
        ))}
      </SpaceBetween>

      <Table
        {...collectionProps}
        items={items}
        empty={
          <Box textAlign="center" padding="l">
            <SpaceBetween size="s" direction="vertical">
              <Box variant="p">No campaign runs yet.</Box>
              <Button variant="primary" onClick={() => onViewRun('')}>
                Run your first campaign →
              </Button>
            </SpaceBetween>
          </Box>
        }
        columnDefinitions={[
          {
            id: 'date',
            header: 'Date & Time',
            sortingField: 'startedAt',
            cell: (item: RunRow) => {
              const val = item.startedAt
              return val ? formatDateTime(val) : '—'
            },
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item: RunRow) => {
              const type = STATUS_TYPE_MAP[item.status]
              const label =
                item.status === 'complete'
                  ? 'Complete'
                  : item.status === 'running'
                    ? 'Running'
                    : item.status === 'error'
                      ? 'Error'
                      : 'Stopped'
              return (
                <SpaceBetween direction="vertical" size="xxxs">
                  <StatusIndicator type={type}>{label}</StatusIndicator>
                  {item.trigger === 'auto' && (
                    <Badge color="blue">Auto</Badge>
                  )}
                </SpaceBetween>
              )
            },
          },
          {
            id: 'contentTypes',
            header: 'Content Types',
            cell: (item: RunRow) => {
              const types = item.selectedContentTypes
              if (!types || types.length === 0) return '—'
              return (
                <SpaceBetween direction="horizontal" size="xxs">
                  {types.map((ct) => (
                    <Badge key={ct} color="grey">
                      {getContentTypeLabel(ct)}
                    </Badge>
                  ))}
                </SpaceBetween>
              )
            },
          },
          {
            id: 'resultsCount',
            header: 'Pieces Generated',
            sortingField: 'resultsCount',
            cell: (item: RunRow) => {
              const count = item.resultsCount
              if (!count) return '—'
              const runId = item.id
              if (!runId) return String(count)
              return (
                <Link
                  onFollow={() =>
                    void navigate(`/workspace/clients/${clientId}/queue?runId=${runId}`)
                  }
                >
                  {String(count)}
                </Link>
              )
            },
          },
          {
            id: 'duration',
            header: 'Duration',
            cell: (item: RunRow) => {
              return formatDuration(item.startedAt, item.completedAt)
            },
          },
          {
            id: 'cost',
            header: 'Cost',
            sortingField: 'estimatedCostUsd',
            cell: (item: RunRow) => {
              const cost = item.estimatedCostUsd
              return cost != null ? `$${cost.toFixed(2)}` : '—'
            },
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item: RunRow) => {
              const runId = item.id ?? ''
              return (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="inline-link"
                    onClick={() => onViewRun(runId)}
                  >
                    View
                  </Button>
                  {item.status === 'error' && (
                    <Button
                      variant="inline-link"
                      disabled={retryLoading}
                      onClick={() => { void handleRetry(item) }}
                    >
                      Retry
                    </Button>
                  )}
                </SpaceBetween>
              )
            },
          },
        ]}
      />

      <Pagination {...paginationProps} />
    </SpaceBetween>
  )
}
